import { unix } from "dayjs"
import {
  useMemo,
  useEffect,
} from "react"

import { useAuthContext } from "../AuthContext"
import { usePageLifecycleContext } from "../PageLifecycleContext"
import { useRefreshAccessToken } from "./useRefreshAccessToken"
import {
  useRefValue,
  useIsLeader,
} from "src/hooks/utils"
import { PageLifecycle } from "src/types"

const oneMinuteInMs = 1000 * 60

export function RefreshTokenHandler() {
  const {
    authCookie,
    authCookieRef,
  } = useAuthContext()

  const refreshAccessToken = useRefreshAccessToken()

  const pageLifecycle = usePageLifecycleContext()

  const isPageFunctional = (
    pageLifecycle === PageLifecycle.Active ||
    pageLifecycle === PageLifecycle.Passive ||
    pageLifecycle === PageLifecycle.Hidden
  )

  const isLeader = useIsLeader(
    "sugarmate-refresh-token",
    isPageFunctional,
  )

  const isLeaderRef = useRefValue(isLeader)

  // a function that refreshes the access token
  const executeRefreshAccessToken = useMemo(
    () => !refreshAccessToken || !isLeader
      ? undefined
      : async () => {
        if (authCookieRef.current && isLeaderRef.current) {
          try {
            await refreshAccessToken(authCookieRef.current.refresh_token)
          } catch {
            // do nothing
          }
        }
      },
    [
      isLeader,
      isLeaderRef,
      authCookieRef,
      refreshAccessToken,
    ],
  )

  /*
   * this schedules a callback for refreshing the access token one minute
   * before the token expires
   */
  useEffect(
    () => {
      // don't schedule a refresh if the user is offline or not logged in
      // or if another tab is scheduling the refresh
      if (
        !authCookie ||
        !executeRefreshAccessToken
      ) {
        return
      }

      // milliseconds until the access token expires
      const timeUntilExpire = unix(authCookie.created_at)
        .add(
          authCookie.expires_in ?? 0,
          "second",
        )
        .diff(
          undefined,
          "millisecond",
        )

      // if the access token is expired or close to expiry then refresh it
      if (timeUntilExpire < oneMinuteInMs) {
        void executeRefreshAccessToken()
        return
      }

      // schedule a timeout to refresh the access token
      const timeoutId = setTimeout(
        () => {
          void executeRefreshAccessToken()
        },
        timeUntilExpire - oneMinuteInMs,
      )

      return () => {
        clearTimeout(timeoutId)
      }
    },
    [
      authCookie,
      executeRefreshAccessToken,
    ],
  )

  return null
}
